import React from 'react';
import { Activity, Building2, Clock, Upload, UserCheck, Users, Scale } from 'lucide-react';

import { Identity } from '../types';

interface NavbarProps {
  identity: Identity;
  /**
   * Local-development only. Switches which seeded subject the `X-Dev-User` header names; the
   * backend refuses that header entirely unless it is running with AUTH_MODE=dev and
   * APP_ENV=local, so this is a convenience, not a privilege escalation path.
   */
  onSwitchDevUser: (subject: string) => void;
  onOpenUpload: () => void;
  onOpenUsers?: () => void;
  onOpenPlatform?: () => void;
  onOpenCorpus?: () => void;
  activeStudyId?: string | null;
}

export const Navbar: React.FC<NavbarProps> = ({
  identity,
  onSwitchDevUser,
  onOpenUpload,
  onOpenUsers,
  onOpenPlatform,
  onOpenCorpus,
  activeStudyId,
}) => {
  // Solid, not glass-panel: a sticky header over translucent, blurred content let scrolled text
  // show through underneath it, garbled — found 2026-09-14 from real use.
  return (
    <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-tr from-cyan-600 to-emerald-400 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Activity className="h-4 w-4 text-white" />
          </div>
          <h1 className="text-sm font-bold tracking-tight text-white flex items-center gap-1.5">
            SCRIBE <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-cyan-950 text-cyan-400 border border-cyan-800/50">v1.0</span>
          </h1>
        </div>

        {activeStudyId && (
          <div className="hidden md:flex items-center space-x-2 bg-slate-900/80 border border-slate-800 px-2.5 py-1 rounded-lg text-xs">
            <Clock className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
            <span className="mono font-semibold text-cyan-300">{activeStudyId}</span>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-3">
        {/*
          * Identity is displayed, not chosen. Roles come from the server, so this shows what
          * the caller actually holds rather than what the client would like to be.
          */}
        <div className="flex items-center space-x-2 rounded-lg border border-slate-800 bg-slate-900 py-1 pl-2 pr-3 text-xs">
          <UserCheck className="h-3.5 w-3.5 text-emerald-400" />
          <div className="leading-tight">
            <div className="font-medium text-slate-200">{identity.full_name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {identity.roles.join(' · ') || 'no roles'}
              {identity.is_delegated && ' · delegated'}
            </div>
          </div>
        </div>

        {/* Local development: switch which seeded subject is being sent. */}
        {import.meta.env.DEV && (
          <select
            value=""
            onChange={(e) => e.target.value && onSwitchDevUser(e.target.value)}
            className="cursor-pointer rounded-lg border border-slate-800 bg-slate-900 px-2 py-1 text-xs text-slate-400 focus:outline-none"
            title="Development only — switches the X-Dev-User subject"
          >
            <option value="">switch dev user…</option>
            <option value="dev-phys" className="bg-slate-900">dev-phys (physician)</option>
            <option value="dev-tech" className="bg-slate-900">dev-tech (technician)</option>
          </select>
        )}

        {/* PLATFORM_OPS only. The server enforces it on every endpoint regardless (§9.7) —
            hiding the entry point is convenience, not a control. */}
        {identity.roles.includes('PLATFORM_OPS') && onOpenPlatform && (
          <button
            onClick={onOpenPlatform}
            className="flex items-center space-x-2 rounded-lg border border-cyan-800 px-2.5 py-1.5 text-xs font-medium text-cyan-300 hover:border-cyan-500 hover:text-white"
          >
            <Building2 className="h-3.5 w-3.5" />
            <span>Organisations</span>
          </button>
        )}

        {/* CORPUS_REVIEWER only (§7.1). The gate is a distinct job from reviewing a study: a
            reader who also signs reports brings the anchoring §7.5 warns about to a screen whose
            whole value is independence. The server enforces the role regardless. */}
        {identity.roles.includes('CORPUS_REVIEWER') && onOpenCorpus && (
          <button
            onClick={onOpenCorpus}
            className="flex items-center space-x-2 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:border-slate-500 hover:text-white"
          >
            <Scale className="h-3.5 w-3.5" />
            <span>Corpus</span>
          </button>
        )}

        {identity.roles.includes('TENANT_ADMIN') && onOpenUsers && (
          <button
            onClick={onOpenUsers}
            className="flex items-center space-x-2 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:border-slate-500 hover:text-white"
          >
            <Users className="h-3.5 w-3.5" />
            <span>Users</span>
          </button>
        )}

        {/* Upload EDF Button */}
        <button
          onClick={onOpenUpload}
          className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-medium text-xs px-3 py-1.5 rounded-lg transition-all shadow-md shadow-cyan-600/20 active:scale-95"
        >
          <Upload className="h-3.5 w-3.5" />
          <span>Upload EDF Study</span>
        </button>
      </div>
    </header>
  );
};
